import { Link } from 'react-router-dom';
import Footer from '../../home/components/Footer';

export default function SmallBusinessTipsArticle() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://static.readdy.ai/image/8dbd60c8f218d42716666e4ddaa548a8/e10d3fd4f000823b1408934f8a9fe8ab.png"
              alt="AMM Management Group"
              className="h-12"
            />
          </Link>
          <Link
            to="/#resources"
            className="text-sm text-[#0a1628] hover:text-[#00e5c7] transition-colors font-semibold"
          >
            ← Back to Resources
          </Link>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-32 pb-24 px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <span className="inline-block px-3 py-1 bg-[#00e5c7] text-[#0a1628] text-xs uppercase tracking-wider rounded-md font-semibold mb-4">
              SMALL BUSINESS
            </span>
            <h1
              className="text-4xl md:text-5xl font-bold text-[#0a1628] mb-6 leading-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Tax Tips for Small Business Owners in 2024
            </h1>
            <div className="flex items-center text-[#5a6a7e] text-sm">
              <span>March 5, 2024</span>
              <span className="mx-3">•</span>
              <span>9 min read</span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/6968d7c8cdbe64027927d1b2a43d157f.jpg"
              alt="Small Business Tax Tips"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-[#5a6a7e] leading-relaxed mb-6">
              Running a small business comes with unique tax challenges and opportunities. Understanding the latest tax laws and implementing smart strategies can save you thousands of dollars while keeping you compliant with IRS regulations. Here are essential tax tips every small business owner should know for 2024.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Choose the Right Business Structure
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Your business structure significantly impacts your tax obligations:
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Sole Proprietorship
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              The simplest structure where business income is reported on your personal tax return (Schedule C). While easy to set up, you're personally liable for all business debts and obligations, and you'll pay self-employment tax on all net profits.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              LLC (Limited Liability Company)
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Offers liability protection while maintaining pass-through taxation. You can choose to be taxed as a sole proprietorship, partnership, S-corp, or C-corp. This flexibility makes LLCs popular among small business owners.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              S Corporation
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Allows you to split income between salary and distributions, potentially reducing self-employment taxes. You must pay yourself a reasonable salary, but distributions aren't subject to self-employment tax. This can result in significant tax savings for profitable businesses.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Maximize Your Deductions
            </h2>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Qualified Business Income (QBI) Deduction
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Pass-through entities may qualify for a deduction of up to 20% of qualified business income. This powerful deduction can significantly reduce your tax bill, but it comes with income limitations and restrictions for certain service businesses.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Section 179 Expensing
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Instead of depreciating equipment over several years, Section 179 allows you to deduct up to $1,160,000 in equipment purchases in 2024. This is ideal for businesses investing in computers, machinery, vehicles, or other qualifying property.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Startup Costs
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              You can deduct up to $5,000 in startup costs in your first year of business, with the remainder amortized over 15 years. Startup costs include market research, advertising, employee training, and professional fees incurred before your business begins operations.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Quarterly Estimated Taxes
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              As a small business owner, you're responsible for paying estimated taxes quarterly. Missing these payments can result in penalties and interest. Here's what you need to know:
            </p>
            <ul className="list-disc pl-6 text-[#5a6a7e] leading-relaxed mb-6 space-y-2">
              <li>Quarterly deadlines: April 15, June 15, September 15, and January 15</li>
              <li>Calculate based on expected annual income or use the safe harbor rule (100% of prior year's tax or 110% if high income)</li>
              <li>Include both income tax and self-employment tax in your estimates</li>
              <li>Adjust estimates if your income changes significantly during the year</li>
              <li>Consider making payments through EFTPS or IRS Direct Pay for convenience</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Employee vs. Independent Contractor
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Properly classifying workers is crucial to avoid penalties:
            </p>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              <strong className="text-[#0a1628]">Employees:</strong> You must withhold income taxes, withhold and pay Social Security and Medicare taxes, and pay unemployment taxes. You'll also need to provide W-2 forms and may be required to offer benefits.
            </p>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              <strong className="text-[#0a1628]">Independent Contractors:</strong> No tax withholding required. You'll issue 1099-NEC forms for payments over $600. Contractors are responsible for their own taxes, but misclassification can result in significant penalties.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Health Insurance Deductions
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Self-employed individuals can deduct 100% of health insurance premiums for themselves, their spouse, and dependents as an adjustment to income. This deduction is available even if you don't itemize deductions. Additionally, consider setting up a Health Savings Account (HSA) if you have a high-deductible health plan. HSA contributions are tax-deductible, grow tax-free, and withdrawals for qualified medical expenses are tax-free.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Record-Keeping and Documentation
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Proper documentation is your best defense in an audit:
            </p>
            <ul className="list-disc pl-6 text-[#5a6a7e] leading-relaxed mb-6 space-y-2">
              <li>Use accounting software to track all income and expenses</li>
              <li>Maintain separate business bank accounts and credit cards</li>
              <li>Keep receipts for all business expenses (digital copies are acceptable)</li>
              <li>Document the business purpose of expenses, especially meals and entertainment</li>
              <li>Maintain mileage logs for vehicle deductions</li>
              <li>Store records for at least 7 years</li>
              <li>Back up digital records regularly</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Year-End Tax Planning
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Don't wait until tax season to think about taxes. Implement these year-end strategies:
            </p>
            <ul className="list-disc pl-6 text-[#5a6a7e] leading-relaxed mb-6 space-y-2">
              <li>Accelerate expenses into the current year if you expect higher income</li>
              <li>Defer income to next year if you expect to be in a lower tax bracket</li>
              <li>Make equipment purchases before year-end to claim Section 179 deductions</li>
              <li>Maximize retirement plan contributions</li>
              <li>Review and adjust estimated tax payments</li>
              <li>Consider year-end bonuses or additional owner compensation</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Conclusion
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Effective tax planning is an ongoing process, not a once-a-year event. By implementing these strategies and working with a qualified tax professional, you can minimize your tax liability, avoid costly mistakes, and focus on growing your business. Remember, tax laws change frequently, so staying informed and seeking professional guidance is essential for long-term success.
            </p>

            <div className="mt-12 p-8 bg-[#0a1628] rounded-lg">
              <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Need Expert Tax Guidance for Your Business?
              </h3>
              <p className="text-white/80 mb-6">
                Our team specializes in helping small business owners navigate complex tax situations and maximize their savings.
              </p>
              <Link
                to="/#contact"
                className="inline-block px-6 py-3 bg-[#00e5c7] text-[#0a1628] rounded-lg font-semibold hover:bg-[#00e5c7]/90 transition-colors whitespace-nowrap"
              >
                Contact Us Today
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
